/** @jsxImportSource @emotion/react */

import { FC, useMemo, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { EditorField } from 'components/Editor'
import { InputField } from 'components/Input'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { validateDefaultHtml, validateDuration, validateRequired } from 'utils/validation'
import { ChipGroupField } from 'components/ChipGroup/ChipGroupField'
import { CourseName, SHORT_SUBJECT_NAMES } from 'theme/utils'
import { TaskTypeEnum } from '__generatedTypes__/globalTypes'
import { findSubjectByPlatform_findSubjectByPlatform_tabs as Subject } from '__generatedTypes__/findSubjectByPlatform'
import { ALL_SUBJECT } from '../List/useTaskFilters'
import { TASK_TYPE_TITLE } from '../consts'
import * as classes from './styles'

export type FormValues = {
  id?: string
  title: string
  subject: string
  type: TaskTypeEnum
  time?: string
  description?: string
  link: string
}

const TYPE_OPTIONS = Object.values(TaskTypeEnum)
  .filter((type) => type !== TaskTypeEnum.Custom)
  .map((type) => ({
    id: type,
    label: TASK_TYPE_TITLE[type],
  }))

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export type Props = {
  subjects: Subject[]
  initialValues?: FormValues
  onSubmit: (values: FormValues) => Promise<void>
  onCancelClick: () => void
  onDelete: (id: string) => void
}

export const Form: FC<Props> = ({ subjects, initialValues, onSubmit, onCancelClick, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false)

  const subjectOptions = useMemo(
    () =>
      [...subjects, ALL_SUBJECT].map((subject) => ({
        id: subject.id,
        label: SHORT_SUBJECT_NAMES[subject.tabName as CourseName] || subject.tabName,
      })),
    [subjects]
  )

  const handleDeleteClick = async () => {
    if (initialValues?.id) {
      try {
        setIsDeleting(true)
        await onDelete(initialValues.id)
      } finally {
        setIsDeleting(false)
      }
    }
  }

  return (
    <FinalForm<FormValues>
      mutators={{ ...arrayMutators }}
      decorators={[focusOnErrors]}
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, initialValues, submitting }) => (
        <form onSubmit={handleSubmit} css={classes.form}>
          <Typography css={classes.title}>{initialValues ? 'Edit Task' : 'New Task'}</Typography>
          <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />
          <ChipGroupField name="subject" label="Subject" validate={validateRequired} options={subjectOptions} />
          <ChipGroupField name="type" label="Type" validate={validateRequired} options={TYPE_OPTIONS} />
          <InputField name="time" validate={validateDuration} label="Time" placeholder="H:MM" fullWidth />
          <EditorField
            data-testid="description"
            type="default"
            name="description"
            label="Description"
            placeholder="Enter Description"
            validate={validateDefaultHtml}
          />
          <InputField name="link" validate={validateRequired} label="Link" placeholder="Insert link" fullWidth />
          <div css={classes.actions}>
            <Button
              variant="outlined"
              color="red"
              size="large"
              css={classes.actionButton}
              onClick={handleDeleteClick}
              disabled={!initialValues || submitting || isDeleting}
            >
              <TrashIcon />
              Delete
            </Button>

            <Button
              variant="outlined"
              color="blue"
              size="large"
              css={classes.actionButton}
              onClick={onCancelClick}
              disabled={submitting || isDeleting}
            >
              Cancel
            </Button>
          </div>
          <Button type="submit" color="blue" variant="contained" size="large" disabled={submitting || isDeleting}>
            Submit
          </Button>
        </form>
      )}
    />
  )
}
